import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';
import { useAdminToast } from '../../hooks/useAdminToast';

import { adminAPI } from '../../services/api';
import CreateClientModal from './components/modals/CreateClientModal';
import MassAssignModal from './components/modals/MassAssignModal';
import MassEmailModal from './components/modals/MassEmailModal';
import MassTagModal from './components/modals/MassTagModal';
import MassNotificationModal from './components/modals/MassNotificationModal';
import MassStatusModal from './components/modals/MassStatusModal';
import AddWalletModal from './components/modals/AddWalletModal';
import BalanceModal from './components/modals/BalanceModal';
import EditClientModal from './components/modals/EditClientModal';
import CreateDepositModal from './components/modals/CreateDepositModal';
import CreateWithdrawalModal from './components/modals/CreateWithdrawalModal';
import CreatePositionModal from './components/modals/CreatePositionModal';
import SpotBalanceModal from './components/modals/SpotBalanceModal';
import SendNotificationModal from './components/modals/SendNotificationModal';
import TradingLockModal from './components/modals/TradingLockModal';

// Tab components
import InfoTab from './components/tabs/InfoTab';
import NotesTab from './components/tabs/NotesTab';
import OrdersTab from './components/tabs/OrdersTab';
import WalletsTab from './components/tabs/WalletsTab';
import PositionsTab from './components/tabs/PositionsTab';
import TransactionsTab from './components/tabs/TransactionsTab';
import IDOTab from './components/tabs/IDOTab';
import HistoryTab from './components/tabs/HistoryTab';
import BalanceHistoryTab from './components/tabs/BalanceHistoryTab';

import {
  Search, ChevronRight, Phone, Mail, DollarSign, X,
  Edit2, Eye, Plus, Minus, TrendingUp, TrendingDown,
  Save, ArrowUpCircle, ArrowDownCircle, User, Wallet,
  FileText, Clock, Settings, Filter, SortAsc, SortDesc,
  Bell, RefreshCw, CheckSquare, Users, Link, ExternalLink, Copy, Tag, Rocket,
  Lock, Unlock, Send, History, ShoppingCart
} from 'lucide-react';

const statusColors = {
  NEW: '#3b82f6',
  IN_WORK: COLORS.primary,
  LOUD: '#8b5cf6',
  OPEN: COLORS.success,
  WITHDRAWAL: COLORS.danger,
  LOUD2: '#ec4899',
  RESET: '#6b7280',
};

// Tag colors mapping
const tagColors = {
  VIP: COLORS.primary,
  Whale: '#3b82f6',
  Active: COLORS.success,
  Inactive: '#6b7280',
  New: '#8b5cf6',
  Problem: COLORS.danger,
  Potential: '#06b6d4',
  Referral: '#ec4899',
  Bonus: '#14b8a6',
};

const AdminClients = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { toast } = useAdminToast();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showMassAssignModal, setShowMassAssignModal] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [showMassEmailModal, setShowMassEmailModal] = useState(false);
  
  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [managerFilter, setManagerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minBalance, setMinBalance] = useState('');
  const [maxBalance, setMaxBalance] = useState('');
  const [minDepositSum, setMinDepositSum] = useState('');
  const [lastActivityDays, setLastActivityDays] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  
  // Exclusion filters
  const [excludeStatus, setExcludeStatus] = useState([]);
  const [excludeTags, setExcludeTags] = useState([]);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  
  // Mass operations
  const [showMassNotificationModal, setShowMassNotificationModal] = useState(false);
  const [showMassStatusModal, setShowMassStatusModal] = useState(false);
  const [showMassTagModal, setShowMassTagModal] = useState(false);
  
  // Create client modal
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  
  // Quick select
  const [quickSelectCount, setQuickSelectCount] = useState('');

  useEffect(() => {
    loadClients();
    loadStatuses();
    loadManagers();
    loadTags();
  }, [search, statusFilter, sortBy, sortOrder, managerFilter, dateFrom, dateTo, minBalance, maxBalance, minDepositSum, lastActivityDays, tagFilter, excludeStatus, excludeTags]);

  // Handle client query param from other pages (e.g., AdminRequests)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client');
    if (clientId) {
      // Always try to load client details directly
      loadClientById(clientId);
      // Clear the query param
      navigate('/admin-panel/clients', { replace: true });
    }
  }, [location.search]);

  const loadClientById = async (clientId) => {
    try {
      const res = await adminAPI.getClientDetails(clientId);
      setClientDetails(res.data);
      setSelectedClient({ id: clientId, ...res.data?.user });
    } catch (err) {
      console.error('Failed to load client:', err);
      toast.error('Не удалось загрузить данные клиента');
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (managerFilter) params.assigned_to = managerFilter;
      if (tagFilter) params.tags = tagFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (minBalance) params.min_balance = parseFloat(minBalance);
      if (maxBalance) params.max_balance = parseFloat(maxBalance);
      if (minDepositSum) params.min_deposit_sum = parseFloat(minDepositSum);
      if (lastActivityDays) params.last_activity_days = parseInt(lastActivityDays);
      
      // Exclusion filters
      if (excludeStatus.length > 0) params.exclude_status = excludeStatus.join(',');
      if (excludeTags.length > 0) params.exclude_tags = excludeTags.join(',');
      
      const res = await adminAPI.getClients(params);
      setClients(res.data.clients || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const res = await adminAPI.getStatuses();
      setStatuses(res.data.statuses || []);
    } catch (err) {
      console.error('Failed to load statuses');
    }
  };

  const loadManagers = async () => {
    try {
      const res = await adminAPI.getManagers();
      setManagers(res.data || []);
    } catch (err) {
      console.error('Failed to load managers');
    }
  };

  const loadTags = async () => {
    try {
      const res = await adminAPI.getAllTags();
      setAvailableTags(res.data.tags || []);
    } catch (err) {
      console.error('Failed to load tags');
    }
  };

  const openClientDetails = async (client) => {
    setSelectedClient(client);
    try {
      const res = await adminAPI.getClientDetails(client.id);
      setClientDetails(res.data);
    } catch (err) {
      console.error('Failed to load client details');
    }
  };

  const closeDetails = () => {
    setSelectedClient(null);
    setClientDetails(null);
  };

  const refreshClient = async () => {
    if (selectedClient) {
      const res = await adminAPI.getClientDetails(selectedClient.id);
      setClientDetails(res.data);
      loadClients();
    }
  };

  const handleQuickSelect = (count) => {
    const ids = clients.slice(0, count).map(c => c.id);
    setSelectedClients(ids);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setManagerFilter('');
    setDateFrom('');
    setDateTo('');
    setMinBalance('');
    setMaxBalance('');
    setMinDepositSum('');
    setLastActivityDays('');
    setTagFilter('');
    setExcludeStatus([]);
    setExcludeTags([]);
    setTagSearchQuery('');
    setSortBy('created_at');
    setSortOrder('desc');
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Access control - check if user can view clients
  if (!isSuperAdmin && !hasPermission('clients', 'view')) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: COLORS.textSecondary 
      }}>
        <Users size={64} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <h2 style={{ color: COLORS.text, marginBottom: '8px' }}>Доступ запрещён</h2>
        <p>У вас нет прав для просмотра списка клиентов</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', margin: 0 }}>
            Клиенты
            <span style={{ color: COLORS.textSecondary, fontSize: '16px', fontWeight: '500', marginLeft: '12px' }}>
              ({total})
            </span>
          </h1>
          
          <button
            onClick={() => setShowCreateClientModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: COLORS.primary,
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <Plus size={18} />
            Создать клиента
          </button>
        </div>
        
        {/* Search & Basic Filters */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <div style={{
            flex: 1,
            minWidth: '250px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: COLORS.card,
            borderRadius: '8px',
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
          }}>
            <Search size={18} color={COLORS.textSecondary} />
            <input
              type="text"
              placeholder="Поиск по email, имени, счёту..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                background: 'none',
                color: COLORS.text,
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '10px 16px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="">Все статусы</option>
            {statuses.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '10px 16px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value="created_at">По дате регистрации</option>
            <option value="balance">По балансу</option>
            <option value="deposit_sum">По сумме депозитов</option>
            <option value="last_login">По активности</option>
            <option value="name">По имени</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '10px 12px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            title={sortOrder === 'asc' ? 'По возрастанию' : 'По убыванию'}
          >
            {sortOrder === 'asc' ? <SortAsc size={18} /> : <SortDesc size={18} />}
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '10px 16px',
              backgroundColor: showFilters ? `${COLORS.primary}20` : COLORS.card,
              border: `1px solid ${showFilters ? COLORS.primary : COLORS.border}`,
              borderRadius: '8px',
              color: showFilters ? COLORS.primary : COLORS.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Filter size={16} />
            Фильтры
            {(excludeStatus.length > 0 || excludeTags.length > 0) && (
              <span style={{
                backgroundColor: COLORS.danger,
                color: '#fff',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: '700',
              }}>
                {excludeStatus.length + excludeTags.length}
              </span>
            )}
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div style={{
            backgroundColor: COLORS.card,
            borderRadius: '12px',
            padding: '16px',
            border: `1px solid ${COLORS.border}`,
            marginBottom: '12px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Менеджер</label>
                <select
                  value={managerFilter}
                  onChange={(e) => setManagerFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                  }}
                >
                  <option value="">Все менеджеры</option>
                  {managers.map(m => (
                    <option key={m.id} value={m.id}>{m.name || m.email}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Дата от</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Дата до</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Баланс от ($)</label>
                <input
                  type="number"
                  value={minBalance}
                  onChange={(e) => setMinBalance(e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Баланс до ($)</label>
                <input
                  type="number"
                  value={maxBalance}
                  onChange={(e) => setMaxBalance(e.target.value)}
                  placeholder="∞"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Мин. сумма депозитов ($)</label>
                <input
                  type="number"
                  value={minDepositSum}
                  onChange={(e) => setMinDepositSum(e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Активность (дней)</label>
                <input
                  type="number"
                  value={lastActivityDays}
                  onChange={(e) => setLastActivityDays(e.target.value)}
                  placeholder="За все время"
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '12px', marginBottom: '6px' }}>Тег</label>
                <select
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '13px',
                  }}
                >
                  <option value="">Все теги</option>
                  {availableTags.map(t => (
                    <option key={t.name} value={t.name}>{t.name} ({t.count})</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Exclusion Filters Row */}
            <div style={{ 
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: COLORS.danger, fontSize: '13px', fontWeight: '600' }}>
                  🚫 Фильтры исключения
                </span>
                {(excludeStatus.length > 0 || excludeTags.length > 0) && (
                  <button
                    onClick={() => { setExcludeStatus([]); setExcludeTags([]); }}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: `${COLORS.danger}15`,
                      border: `1px solid ${COLORS.danger}40`,
                      borderRadius: '6px',
                      color: COLORS.danger,
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    Сбросить исключения ({excludeStatus.length + excludeTags.length})
                  </button>
                )}
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Exclude Statuses */}
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: `${COLORS.danger}05`, 
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.danger}15`,
                }}>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Исключить по статусу
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {statuses.map(s => (
                      <button
                        key={s}
                        onClick={() => {
                          if (excludeStatus.includes(s)) {
                            setExcludeStatus(excludeStatus.filter(st => st !== s));
                          } else {
                            setExcludeStatus([...excludeStatus, s]);
                          }
                        }}
                        style={{
                          padding: '5px 12px',
                          backgroundColor: excludeStatus.includes(s) ? COLORS.danger : COLORS.card,
                          border: `1px solid ${excludeStatus.includes(s) ? COLORS.danger : COLORS.border}`,
                          borderRadius: '16px',
                          color: excludeStatus.includes(s) ? '#fff' : COLORS.textSecondary,
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: excludeStatus.includes(s) ? '600' : '400',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {excludeStatus.includes(s) ? '✕ ' : ''}{s}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Exclude Tags with Search */}
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: `${COLORS.danger}05`, 
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.danger}15`,
                }}>
                  <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase' }}>
                    Исключить по тегу
                  </label>
                  
                  {/* Tag Search Input */}
                  <div style={{ position: 'relative', marginBottom: '8px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
                    <input
                      type="text"
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      placeholder="Поиск тегов..."
                      style={{
                        width: '100%',
                        padding: '6px 10px 6px 30px',
                        backgroundColor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        color: COLORS.text,
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  
                  {/* Selected excluded tags */}
                  {excludeTags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                      {excludeTags.map(tag => (
                        <span
                          key={tag}
                          onClick={() => setExcludeTags(excludeTags.filter(t => t !== tag))}
                          style={{
                            padding: '3px 8px',
                            backgroundColor: COLORS.danger,
                            borderRadius: '12px',
                            color: '#fff',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          ✕ {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Available tags (filtered by search) */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxHeight: '70px', overflowY: 'auto' }}>
                    {availableTags
                      .filter(t => !excludeTags.includes(t.name))
                      .filter(t => !tagSearchQuery || t.name.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                      .map(t => (
                        <button
                          key={t.name}
                          onClick={() => setExcludeTags([...excludeTags, t.name])}
                          style={{
                            padding: '3px 8px',
                            backgroundColor: COLORS.card,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: '12px',
                            color: COLORS.textSecondary,
                            fontSize: '11px',
                            cursor: 'pointer',
                          }}
                        >
                          {t.name} <span style={{ opacity: 0.6 }}>({t.count})</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Clear All Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  color: COLORS.textSecondary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <RefreshCw size={14} />
                Сбросить всё
              </button>
            </div>
          </div>
        )}

        {/* Quick Select */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>Быстрый выбор:</span>
          {[10, 25, 50, 100].map(count => (
            <button
              key={count}
              onClick={() => handleQuickSelect(count)}
              style={{
                padding: '6px 12px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                color: COLORS.textSecondary,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {count}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="number"
              value={quickSelectCount}
              onChange={(e) => setQuickSelectCount(e.target.value)}
              placeholder="N"
              style={{
                width: '60px',
                padding: '6px 10px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                color: COLORS.text,
                fontSize: '12px',
              }}
            />
            <button
              onClick={() => quickSelectCount && handleQuickSelect(parseInt(quickSelectCount))}
              style={{
                padding: '6px 12px',
                backgroundColor: COLORS.primary,
                border: 'none',
                borderRadius: '6px',
                color: '#000',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: '600',
              }}
            >
              Выбрать
            </button>
          </div>
        </div>
      </div>

      {/* Mass Actions Bar */}
      {selectedClients.length > 0 && (
        <div style={{
          backgroundColor: COLORS.primary,
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <span style={{ color: '#000', fontWeight: '600' }}>
            Выбрано: {selectedClients.length} клиент(ов)
          </span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Manager Assignment - requires clients.assign */}
            {hasPermission('clients', 'assign') && (
              <button
                onClick={() => setShowMassAssignModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  color: COLORS.primary,
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <Users size={14} />
                Менеджер
              </button>
            )}
            {/* Notification - requires clients.send_email */}
            {hasPermission('clients', 'send_email') && (
              <button
                onClick={() => setShowMassNotificationModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#3b82f6',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <Bell size={14} />
                Уведомление
              </button>
            )}
            {/* Status change - requires clients.change_status */}
            {hasPermission('clients', 'change_status') && (
              <button
                onClick={() => setShowMassStatusModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#8b5cf6',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <CheckSquare size={14} />
              Статус
            </button>
            )}
            {/* Tag - requires clients.add_tags */}
            {hasPermission('clients', 'add_tags') && (
              <button
                onClick={() => setShowMassTagModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  color: COLORS.primary,
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <Tag size={14} />
                Тег
              </button>
            )}
            {/* Email - requires clients.send_email */}
            {hasPermission('clients', 'send_email') && (
              <button
                onClick={() => setShowMassEmailModal(true)}
                style={{
                  padding: '8px 14px',
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ec4899',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                }}
              >
                <Mail size={14} />
                Email
              </button>
            )}
            <button
              onClick={() => setSelectedClients([])}
              style={{
                padding: '8px 14px',
                backgroundColor: 'transparent',
                border: '1px solid #000',
                borderRadius: '6px',
                color: '#000',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div style={{
        flex: 1,
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 80px',
          padding: '14px 20px',
          backgroundColor: COLORS.background,
          borderBottom: `1px solid ${COLORS.border}`,
          gap: '12px',
          alignItems: 'center',
        }}>
          <input
            type="checkbox"
            checked={selectedClients.length === clients.length && clients.length > 0}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedClients(clients.map(c => c.id));
              } else {
                setSelectedClients([]);
              }
            }}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>КЛИЕНТ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>БАЛАНС</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СТАТУС</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ТЕГИ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>МЕНЕДЖЕР</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', textAlign: 'center' }}></span>
        </div>

        {/* Table Body */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 350px)' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
              Загрузка...
            </div>
          ) : clients.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
              Клиенты не найдены
            </div>
          ) : (
            clients.map((client) => (
              <div
                key={client.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 2fr 1fr 1fr 1fr 1fr 80px',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${COLORS.border}20`,
                  gap: '12px',
                  alignItems: 'center',
                  transition: 'background-color 0.2s',
                  backgroundColor: selectedClients.includes(client.id) ? `${COLORS.primary}10` : 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = selectedClients.includes(client.id) ? `${COLORS.primary}15` : COLORS.background}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedClients.includes(client.id) ? `${COLORS.primary}10` : 'transparent'}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedClients.includes(client.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (e.target.checked) {
                      setSelectedClients([...selectedClients, client.id]);
                    } else {
                      setSelectedClients(selectedClients.filter(id => id !== client.id));
                    }
                  }}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                {/* Client Info */}
                <div 
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                  onClick={() => openClientDetails(client)}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: `${COLORS.primary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: COLORS.primary,
                    fontWeight: '700',
                    fontSize: '14px',
                  }}>
                    {client.email?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                        {client.name || client.email}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(client.email);
                        }}
                        title="Копировать email"
                        style={{
                          padding: '2px 4px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: COLORS.textSecondary,
                          cursor: 'pointer',
                          opacity: 0.6,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Copy size={12} />
                      </button>
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                      {client.account_number || client.email}
                    </div>
                  </div>
                </div>

                {/* Balance */}
                <div style={{ color: COLORS.success, fontSize: '14px', fontWeight: '600' }}>
                  ${(client.balance || 0).toFixed(2)}
                </div>

                {/* Status */}
                <div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: `${statusColors[client.client_status] || COLORS.textSecondary}20`,
                    color: statusColors[client.client_status] || COLORS.textSecondary,
                    fontSize: '12px',
                    fontWeight: '600',
                  }}>
                    {client.client_status || 'NEW'}
                  </span>
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(client.tags || []).slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: `${tagColors[tag] || '#6b7280'}20`,
                        color: tagColors[tag] || '#6b7280',
                        fontSize: '10px',
                        fontWeight: '600',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                  {(client.tags || []).length > 3 && (
                    <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>
                      +{client.tags.length - 3}
                    </span>
                  )}
                </div>

                {/* Manager */}
                <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
                  {client.manager_name || '-'}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); openClientDetails(client); }}
                    style={{
                      padding: '6px',
                      backgroundColor: `${COLORS.primary}15`,
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <Eye size={16} color={COLORS.primary} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Client Details Modal */}
      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          details={clientDetails}
          statuses={statuses}
          managers={managers}
          onClose={closeDetails}
          onRefresh={refreshClient}
          currentUserId={currentUser?.id || currentUser?.user_id}
        />
      )}

      {/* Mass Assign Modal */}
      {showMassAssignModal && (
        <MassAssignModal
          selectedClients={selectedClients}
          managers={managers}
          currentUser={currentUser}
          statuses={statuses}
          availableTags={availableTags}
          onClose={() => setShowMassAssignModal(false)}
          onSuccess={() => {
            setShowMassAssignModal(false);
            setSelectedClients([]);
            loadClients();
          }}
        />
      )}

      {/* Mass Notification Modal */}
      {showMassNotificationModal && (
        <MassNotificationModal
          selectedClients={selectedClients}
          onClose={() => setShowMassNotificationModal(false)}
          onSuccess={() => {
            setShowMassNotificationModal(false);
            setSelectedClients([]);
          }}
        />
      )}

      {/* Mass Status Modal */}
      {showMassStatusModal && (
        <MassStatusModal
          selectedClients={selectedClients}
          statuses={statuses}
          onClose={() => setShowMassStatusModal(false)}
          onSuccess={() => {
            setShowMassStatusModal(false);
            setSelectedClients([]);
            loadClients();
          }}
        />
      )}

      {/* Mass Tag Modal */}
      {showMassTagModal && (
        <MassTagModal
          selectedClients={selectedClients}
          availableTags={availableTags}
          onClose={() => setShowMassTagModal(false)}
          onSuccess={() => {
            setShowMassTagModal(false);
            setSelectedClients([]);
            loadClients();
            loadTags();
          }}
        />
      )}

      {/* Mass Email Modal */}
      {showMassEmailModal && (
        <MassEmailModal
          selectedClients={selectedClients}
          onClose={() => setShowMassEmailModal(false)}
          onSuccess={() => {
            setShowMassEmailModal(false);
            setSelectedClients([]);
          }}
        />
      )}
      
      {/* Create Client Modal */}
      {showCreateClientModal && (
        <CreateClientModal
          managers={managers}
          onClose={() => setShowCreateClientModal(false)}
          onSuccess={() => {
            setShowCreateClientModal(false);
            loadClients();
          }}
        />
      )}
    </div>
  );
};

// ================== CLIENT DETAILS MODAL ==================

const ClientDetailsModal = ({ client, details, statuses, managers, onClose, onRefresh, currentUserId }) => {
  const { toast } = useAdminToast();
  const [activeTab, setActiveTab] = useState('info');
  const [newNote, setNewNote] = useState('');
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showSpotBalanceModal, setShowSpotBalanceModal] = useState(false);
  const [showAddWalletModal, setShowAddWalletModal] = useState(false);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showTradingLockModal, setShowTradingLockModal] = useState(false);
  
  // Check if trading is blocked
  const isTradingBlocked = client?.trading_blocked || details?.user?.trading_blocked || false;

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      // Try to get user ID from multiple sources
      const userId = currentUserId || 
        JSON.parse(localStorage.getItem('user') || '{}')?.id ||
        JSON.parse(localStorage.getItem('user') || '{}')?.user_id;
      await adminAPI.addCRMNote(client.id, newNote, userId);
      setNewNote('');
      onRefresh();
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.apiError(err, 'Ошибка при добавлении заметки');
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await adminAPI.updateClient(client.id, { client_status: newStatus });
      onRefresh();
    } catch (err) {
      console.error('Failed to update status');
    }
  };

  const tabs = [
    { id: 'info', label: 'Инфо', icon: User },
    { id: 'ido', label: 'IDO', icon: Rocket },
    { id: 'orders', label: 'Ордера', icon: ShoppingCart },
    { id: 'wallets', label: 'Кошельки', icon: Link },
    { id: 'positions', label: 'Позиции', icon: TrendingUp },
    { id: 'transactions', label: 'Операции', icon: Wallet },
    { id: 'balance-history', label: 'История $', icon: History },
    { id: 'notes', label: 'Заметки', icon: FileText },
    { id: 'history', label: 'Действия', icon: Clock },
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'flex-end',
      zIndex: 1000,
    }} onClick={onClose}>
      <div
        style={{
          width: '100%',
          maxWidth: '900px',
          height: '95vh',
          maxHeight: '1000px',
          backgroundColor: COLORS.card,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>
              {details?.user?.name || details?.user?.email}
            </h2>
            <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginTop: '4px' }}>
              {details?.user?.account_number} • {details?.user?.email}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        {/* Quick Actions & Balance */}
        <div style={{ padding: '16px' }}>
          {/* Balance Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '10px',
            marginBottom: '12px',
          }}>
            {/* Main Balance */}
            <div style={{
              backgroundColor: COLORS.background,
              borderRadius: '10px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Баланс</div>
              <div style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700' }}>
                ${(details?.balance?.exchange_balance || 0).toFixed(2)}
              </div>
            </div>
            {/* Spot Balance */}
            <div style={{
              backgroundColor: COLORS.background,
              borderRadius: '10px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>Spot</div>
              <div style={{ color: COLORS.success, fontSize: '20px', fontWeight: '700' }}>
                ${(details?.balance?.spot_balance || 0).toFixed(2)}
              </div>
            </div>
            {/* IDO Balance */}
            <div style={{
              backgroundColor: COLORS.background,
              borderRadius: '10px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>IDO</div>
              <div style={{ color: COLORS.primary, fontSize: '20px', fontWeight: '700' }}>
                ${(details?.ido_balance || 0).toFixed(2)}
              </div>
            </div>
            {/* PnL */}
            <div style={{
              backgroundColor: COLORS.background,
              borderRadius: '10px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>PnL (нереализ.)</div>
              <div style={{ 
                color: (details?.balance?.total_pnl || 0) >= 0 ? COLORS.success : COLORS.danger, 
                fontSize: '20px', 
                fontWeight: '700' 
              }}>
                {(details?.balance?.total_pnl || 0) >= 0 ? '+' : ''}${(details?.balance?.total_pnl || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginTop: '4px' }}>
                Spot: {(details?.balance?.spot_pnl || 0) >= 0 ? '+' : ''}${(details?.balance?.spot_pnl || 0).toFixed(2)} | 
                Позиции: {(details?.balance?.open_pnl || 0) >= 0 ? '+' : ''}${(details?.balance?.open_pnl || 0).toFixed(2)}
              </div>
            </div>
            {/* Realized PnL */}
            <div style={{
              backgroundColor: COLORS.background,
              borderRadius: '10px',
              padding: '12px',
            }}>
              <div style={{ color: COLORS.textSecondary, fontSize: '11px', marginBottom: '4px' }}>PnL (реализ.)</div>
              <div style={{ 
                color: (details?.balance?.realized_pnl || 0) >= 0 ? COLORS.success : COLORS.danger, 
                fontSize: '20px', 
                fontWeight: '700' 
              }}>
                {(details?.balance?.realized_pnl || 0) >= 0 ? '+' : ''}${(details?.balance?.realized_pnl || 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Status & Actions */}
          <div style={{
            backgroundColor: COLORS.background,
            borderRadius: '10px',
            padding: '12px',
            marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div>
                  <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Статус</div>
                </div>
                {isTradingBlocked && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px',
                    padding: '4px 8px',
                    backgroundColor: `${COLORS.danger}20`,
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.danger}`,
                  }}>
                    <Lock size={12} color={COLORS.danger} />
                    <span style={{ color: COLORS.danger, fontSize: '11px', fontWeight: '600' }}>Торговля заблокирована</span>
                  </div>
                )}
              </div>
              <select
                value={details?.user?.client_status || 'NEW'}
                onChange={(e) => updateStatus(e.target.value)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: `${statusColors[details?.user?.client_status] || COLORS.textSecondary}20`,
                  border: `1px solid ${statusColors[details?.user?.client_status] || COLORS.textSecondary}`,
                  borderRadius: '6px',
                  color: statusColors[details?.user?.client_status] || COLORS.textSecondary,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                {statuses.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons - Compact horizontal layout */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              <ActionButton icon={DollarSign} label="Баланс" color="#0ecb81" onClick={() => setShowBalanceModal(true)} />
              <ActionButton icon={Wallet} label="Spot" color="#10b981" onClick={() => setShowSpotBalanceModal(true)} />
              <ActionButton icon={ArrowUpCircle} label="Депозит" color="#3b82f6" onClick={() => setShowDepositModal(true)} />
              <ActionButton icon={ArrowDownCircle} label="Вывод" color="#f6465d" onClick={() => setShowWithdrawalModal(true)} />
              <ActionButton icon={TrendingUp} label="Позиция" color="#f0b90b" onClick={() => setShowPositionModal(true)} />
              <ActionButton icon={Edit2} label="Изменить" color="#8b5cf6" onClick={() => setShowEditModal(true)} />
              <ActionButton icon={Send} label="Уведом." color="#06b6d4" onClick={() => setShowNotificationModal(true)} />
              <ActionButton icon={isTradingBlocked ? Unlock : Lock} label={isTradingBlocked ? 'Разблок.' : 'Блок.'} color={isTradingBlocked ? '#22c55e' : '#ef4444'} onClick={() => setShowTradingLockModal(true)} />
            </div>
          </div>
        </div>

        {/* Tabs - with horizontal scroll */}
        <div style={{ 
          display: 'flex', 
          borderBottom: `1px solid ${COLORS.border}`, 
          padding: '0 12px',
          overflowX: 'auto',
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'thin',
          msOverflowStyle: 'none',
          paddingBottom: '2px',
        }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                  color: activeTab === tab.id ? COLORS.primary : COLORS.textSecondary,
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  flexShrink: 0,
                }}
              >
                <Icon size={12} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
          {activeTab === 'info' && <InfoTab details={details} />}
          {activeTab === 'ido' && <IDOTab clientId={client.id} onRefresh={onRefresh} />}
          {activeTab === 'orders' && <OrdersTab clientId={client.id} details={details} />}
          {activeTab === 'wallets' && <WalletsTab clientId={client.id} details={details} onAddWallet={() => setShowAddWalletModal(true)} />}
          {activeTab === 'positions' && <PositionsTab details={details} onRefresh={onRefresh} clientId={client.id} />}
          {activeTab === 'transactions' && <TransactionsTab details={details} onRefresh={onRefresh} />}
          {activeTab === 'balance-history' && <BalanceHistoryTab clientId={client.id} />}
          {activeTab === 'notes' && <NotesTab details={details} newNote={newNote} setNewNote={setNewNote} addNote={addNote} />}
          {activeTab === 'history' && <HistoryTab details={details} />}
        </div>

        {/* Sub-Modals */}
        {showBalanceModal && (
          <BalanceModal clientId={client.id} currentBalance={details?.balance?.exchange_balance || 0} onClose={() => setShowBalanceModal(false)} onSuccess={() => { setShowBalanceModal(false); onRefresh(); }} />
        )}
        {showAddWalletModal && (
          <AddWalletModal clientId={client.id} onClose={() => setShowAddWalletModal(false)} onSuccess={() => { setShowAddWalletModal(false); onRefresh(); }} />
        )}
        {showEditModal && (
          <EditClientModal client={details?.user} managers={managers} statuses={statuses} onClose={() => setShowEditModal(false)} onSuccess={() => { setShowEditModal(false); onRefresh(); }} />
        )}
        {showDepositModal && (
          <CreateDepositModal 
            clientId={client.id} 
            clientWallets={details?.wallets || []} 
            onClose={() => setShowDepositModal(false)} 
            onSuccess={() => { setShowDepositModal(false); onRefresh(); }} 
          />
        )}
        {showWithdrawalModal && (
          <CreateWithdrawalModal clientId={client.id} balance={details?.balance?.exchange_balance || 0} onClose={() => setShowWithdrawalModal(false)} onSuccess={() => { setShowWithdrawalModal(false); onRefresh(); }} />
        )}
        {showPositionModal && (
          <CreatePositionModal clientId={client.id} clientBalance={details?.balance?.exchange_balance || 0} onClose={() => setShowPositionModal(false)} onSuccess={() => { setShowPositionModal(false); onRefresh(); }} />
        )}
        {showSpotBalanceModal && (
          <SpotBalanceModal clientId={client.id} holdings={details?.holdings || []} onClose={() => setShowSpotBalanceModal(false)} onSuccess={() => { setShowSpotBalanceModal(false); onRefresh(); }} />
        )}
        {showNotificationModal && (
          <SendNotificationModal clientId={client.id} clientEmail={details?.user?.email} onClose={() => setShowNotificationModal(false)} onSuccess={() => { setShowNotificationModal(false); onRefresh(); }} />
        )}
        {showTradingLockModal && (
          <TradingLockModal clientId={client.id} isCurrentlyLocked={isTradingBlocked} onClose={() => setShowTradingLockModal(false)} onSuccess={() => { setShowTradingLockModal(false); onRefresh(); }} />
        )}
      </div>
    </div>
  );
};

// ================== ACTION BUTTON ==================
const ActionButton = ({ icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 12px',
      backgroundColor: `${color}15`,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '6px',
    }}
  >
    <Icon size={14} color={color} />
    <span style={{ fontSize: '11px', color: COLORS.text, fontWeight: '500' }}>{label}</span>
  </button>
);


// ================== TAB COMPONENTS ==================
// All tab components have been extracted to ./components/tabs/
// - InfoTab, OrdersTab, WalletsTab, PositionsTab, TransactionsTab, NotesTab, IDOTab, HistoryTab

// Note: ModalWrapper & FormInput now imported from ./components/modals/ModalComponents
// These local copies are kept for backwards compatibility with remaining inline components
const ModalWrapper = ({ title, children, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
    <div style={{ backgroundColor: COLORS.card, borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);

const FormInput = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ width: '100%', padding: '12px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', boxSizing: 'border-box' }} />
  </div>
);

export default AdminClients;
